import { useCallback, useEffect, useMemo, useRef, useState, type FC } from "react";

import { useGetFreeSpinProviders } from "../../../../queries/free-spins/useGetFreeSpinProviders";

import styles from "./FreeSpinSelect.module.css";

const humanize = (id: string): string =>
  id
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");

const PAGE_SIZE = 20;

export type FreeSpinProviderSelectProps = {
  value?: string;
  onChange?: (value: string | undefined) => void;
  disabled?: boolean;
  "data-testid"?: string;
};

/**
 * Single-select searchable dropdown for free-spin-eligible casino providers.
 * Controlled via `value` / `onChange` to work inside Ant Design `<Form.Item>`.
 */
const FreeSpinProviderSelect: FC<FreeSpinProviderSelectProps> = ({
  value,
  onChange,
  disabled = false,
  ...rest
}) => {
  const testId = rest["data-testid"] ?? "free-spin-provider-select";
  const rootRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  // Fetch eagerly — the provider list is tiny (~3 items) and cached for 5 min.
  const providersQuery = useGetFreeSpinProviders(!disabled);
  const isLoadingProviders =
    providersQuery.isLoading || providersQuery.isFetching || !providersQuery.data;

  const closePanel = useCallback(() => {
    setOpen(false);
    setSearch("");
    setVisibleCount(PAGE_SIZE);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) closePanel();
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [open, closePanel]);

  const filtered = useMemo(() => {
    const allProviders = providersQuery.data ?? [];
    if (!search.trim()) return allProviders;
    const q = search.trim().toLowerCase();
    return allProviders.filter(
      (p) => p.toLowerCase().includes(q) || humanize(p).toLowerCase().includes(q),
    );
  }, [providersQuery.data, search]);

  const visible = filtered.slice(0, visibleCount);
  const hasMore = filtered.length > visibleCount;

  const handleSelect = (provider: string) => {
    onChange?.(provider);
    closePanel();
  };

  const handleRemove = () => {
    onChange?.(undefined);
  };

  return (
    <div className={styles.root} ref={rootRef} data-testid={testId}>
      <div className={`${styles.triggerWrap} ${open ? styles.triggerOpen : ""}`}>
        <input
          type="text"
          role="combobox"
          className={styles.trigger}
          value={search}
          placeholder={value ? humanize(value) : "Search providers…"}
          onChange={(e) => {
            setSearch(e.target.value);
            setVisibleCount(PAGE_SIZE);
            if (!open) setOpen(true);
          }}
          onFocus={() => !disabled && setOpen(true)}
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={`${testId}-panel`}
          data-testid={`${testId}-trigger`}
        />
        <button
          type="button"
          className={styles.chevron}
          onClick={() => {
            if (disabled) return;
            if (open) closePanel();
            else setOpen(true);
          }}
          disabled={disabled}
          tabIndex={-1}
          aria-label="Toggle provider list"
          data-testid={`${testId}-toggle`}
        >
          {open ? "\u2303" : "\u2304"}
        </button>
      </div>

      {value && (
        <div className={styles.chips} data-testid={`${testId}-chips`}>
          <span className={styles.chip} data-testid={`${testId}-chip-${value}`}>
            <span className={styles.chipLabel}>{humanize(value)}</span>
            {!disabled && (
              <button
                type="button"
                className={styles.chipRemove}
                onClick={handleRemove}
                aria-label={`Remove ${humanize(value)}`}
                data-testid={`${testId}-chip-remove`}
              >
                {"\u00d7"}
              </button>
            )}
          </span>
        </div>
      )}

      {open && !disabled && (
        <div
          id={`${testId}-panel`}
          className={styles.panel}
          role="listbox"
          aria-label="Free spin providers"
          data-testid={`${testId}-panel`}
        >
          {isLoadingProviders && !providersQuery.isError ? (
            <div className={styles.statusRow} data-testid={`${testId}-loading`}>
              Loading providers…
            </div>
          ) : providersQuery.isError ? (
            <div className={styles.statusRow} data-testid={`${testId}-error`}>
              <span>Failed to load providers</span>
              <button
                type="button"
                className={styles.retryButton}
                onClick={() => providersQuery.refetch()}
                data-testid={`${testId}-retry`}
              >
                Retry
              </button>
            </div>
          ) : filtered.length === 0 ? (
            <div className={styles.statusRow} data-testid={`${testId}-empty`}>
              {search.trim() ? "No matching providers" : "No providers available"}
            </div>
          ) : (
            <div className={styles.optionList}>
              {visible.map((provider) => (
                <button
                  key={provider}
                  type="button"
                  role="option"
                  aria-selected={provider === value}
                  className={`${styles.optionRow} ${provider === value ? styles.optionSelected : ""}`}
                  onClick={() => handleSelect(provider)}
                  data-testid={`${testId}-option-${provider}`}
                >
                  {humanize(provider)}
                </button>
              ))}
              {hasMore && (
                <button
                  type="button"
                  className={styles.showMore}
                  onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
                  data-testid={`${testId}-show-more`}
                >
                  Show more ({filtered.length - visibleCount} remaining)
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FreeSpinProviderSelect;
